import {FC, useEffect, useRef, useState} from 'react'
import clsx from 'clsx'
import {Dropdown} from 'react-bootstrap'
import {useAuth} from '../auth'
import {shouldAnnouncePrintPage} from '../apps/invoice-management/invoice-print/intro/announceAccess'
import {openPrintPageIntro} from '../apps/invoice-management/invoice-print/intro/printPageIntroEvent'
import {openInstallGuide} from '../install-app/core/installPrompt'
import {
  installedAppName,
  installOfferLine,
  thisDevice,
  useInstallState,
} from '../install-app/core/useInstallState'
import {openProformaIntro, PROFORMA_ANNOUNCEMENT, shouldAnnounceProforma} from './proformaIntro'
import {BUYER_LINKS_ANNOUNCEMENT, openBuyerLinksIntro, shouldAnnounceBuyerLinks} from './buyerLinksIntro'
import {
  INVOICE_ALERTS_ANNOUNCEMENT,
  openInvoiceAlertsIntro,
  openPeriodicReportsIntro,
  openTaxWorkspaceAlertsIntro,
  PERIODIC_REPORTS_ANNOUNCEMENT,
  shouldAnnounceBusinessServices,
  TAX_WORKSPACE_ALERTS_ANNOUNCEMENT,
} from './businessServicesIntro'
import {
  openTaxPublicQrIntro,
  shouldAnnounceTaxPublicQr,
  TAX_PUBLIC_QR_ANNOUNCEMENT,
} from './taxPublicQrIntro'
import {loadSeenAnnouncements, saveSeenAnnouncements, unseenAnnouncements} from './whatsNewSeen'
import './whatsNew.scss'

type WhatsNewItem = {
  id: string
  icon: string
  title: string
  text: string
  open: () => void
  /** Set for an item that shakes the header's icon until the list is opened (whatsNewSeen). */
  announcement?: string
}

/**
 * «امکانات تازه» in the header: the list of what is new for this user, each item opening its own
 * introduction. Today: invoice alerts and periodic reports, buyer links, the proforma, the tax-workspace alerts and the
 * organisation's QR on prints (whose arrival shakes the icon instead of opening a
 * window at sign-in), the print page and template designer (for whoever it is announced to) and installing
 * the panel as an app (while it is not installed on this device). Hidden when nothing is new for
 * this user.
 */
export const WhatsNewMenu: FC<{itemClassName?: string; buttonClassName?: string}> = ({
  itemClassName,
  buttonClassName,
}) => {
  const {currentUser, saveAuth, setCurrentUser} = useAuth()
  const install = useInstallState()
  const [open, setOpen] = useState(false)
  // Unknown until read; a failed read never shakes the icon.
  const [seen, setSeen] = useState<string[]>()
  // What was new when the list was opened: marked «جدید» while it stays open.
  const [freshInList, setFreshInList] = useState<string[]>([])

  const items: WhatsNewItem[] = []
  if (shouldAnnounceBusinessServices(currentUser)) {
    items.push(
      {
        id: 'invoice-alerts',
        icon: 'la-bell',
        title: 'اعلان‌های صورتحساب',
        text: 'واکنش خریدار در کارپوشه، ماده ۶، خطای ارسال و پایان ارسال گروهی را همان لحظه بدانید؛ اگر بخواهید با پیامک.',
        open: openInvoiceAlertsIntro,
        announcement: INVOICE_ALERTS_ANNOUNCEMENT,
      },
      {
        id: 'periodic-reports',
        icon: 'la-chart-bar',
        title: 'گزارش دوره‌ای',
        text: 'گزارش پیامکی درج و ارسال صورتحساب‌ها، هر ۶ ساعت تا هر ۳۰ روز، با گزارش کامل در پنل.',
        open: openPeriodicReportsIntro,
        announcement: PERIODIC_REPORTS_ANNOUNCEMENT,
      }
    )
  }
  if (shouldAnnounceBuyerLinks(currentUser)) {
    items.push({
      id: 'buyer-links',
      icon: 'la-share-alt',
      title: 'لینک خریدار',
      text: 'صورتحساب را با پیامک یا لینک برای خریدار بفرستید؛ تأیید، رد، نظر و پرداخت او را کنار هر صورتحساب ببینید.',
      open: openBuyerLinksIntro,
      announcement: BUYER_LINKS_ANNOUNCEMENT,
    })
  }
  if (shouldAnnounceProforma(currentUser)) {
    items.push({
      id: 'proforma',
      icon: 'la-file-signature',
      title: 'پیش‌فاکتور',
      text: 'پیشنهاد قیمت با تاریخ اعتبار که با یک کلیک صورتحساب می‌شود؛ ارسال نمی‌شود و در جمع‌ها نمی‌آید.',
      open: openProformaIntro,
      announcement: PROFORMA_ANNOUNCEMENT,
    })
  }
  // 2026-10-17: listed after the business's earlier announcements, which keep their places.
  if (shouldAnnounceBusinessServices(currentUser)) {
    items.push({
      id: 'tax-workspace-alerts',
      icon: 'la-inbox',
      title: 'اعلان‌های کارپوشه و تأیید خودکار',
      text: 'صورتحساب‌های خرید و قراردادهای منتظر واکنش در کارپوشه را بدانید و خریدهای فروشندگان مطمئن را خودکار تأیید کنید.',
      open: openTaxWorkspaceAlertsIntro,
      announcement: TAX_WORKSPACE_ALERTS_ANNOUNCEMENT,
    })
  }
  // 2026-10-26: after the earlier announcements, which keep their places.
  if (shouldAnnounceTaxPublicQr(currentUser)) {
    items.push({
      id: 'tax-public-qr',
      icon: 'la-qrcode',
      title: 'QR سامانه مؤدیان روی چاپ',
      text: 'چاپ صورتحساب ثبت‌شده، QR صفحهٔ همان صورتحساب در سایت سازمان را هم دارد؛ خریدار با اسکن آن، صورتحساب را آنجا می‌بیند.',
      open: openTaxPublicQrIntro,
      announcement: TAX_PUBLIC_QR_ANNOUNCEMENT,
    })
  }
  if (shouldAnnouncePrintPage(currentUser)) {
    items.push({
      id: 'print',
      icon: 'la-print',
      title: 'ویرایشگر قالب چاپ و صفحهٔ چاپ جدید',
      text: 'لوگو، مهر و امضا و ظاهر برگه را خودتان تنظیم کنید؛ خریدار صورتحساب را بدون ورود به پنل می‌بیند.',
      open: openPrintPageIntro,
    })
  }
  if (!install.installed && !currentUser?.support) {
    items.push({
      id: 'install',
      icon: 'la-download',
      title: `نصب ${installedAppName()} روی ${thisDevice(install.detected)}`,
      text: installOfferLine(install.detected, install.guide),
      open: openInstallGuide,
    })
  }

  const announced = items.flatMap((item) => (item.announcement ? [item.announcement] : []))
  const announcedKey = announced.join(',')
  const userId = Number(currentUser?.id || 0)
  const unseen = unseenAnnouncements(announced, seen)
  // The Dropdown may call onToggle with the handler of an earlier render: it reads these from here.
  const latest = useRef({seen, unseen})
  latest.current = {seen, unseen}

  useEffect(() => {
    if (!announcedKey || userId <= 0) return
    let active = true
    setSeen(undefined)
    loadSeenAnnouncements()
      .then((ids) => {
        if (active) setSeen(ids)
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [announcedKey, userId])

  if (!items.length) return null

  const markSeen = () => {
    const {seen: known, unseen: fresh} = latest.current
    setFreshInList(fresh)
    if (!known || !fresh.length) return
    const next = Array.from(new Set([...known, ...fresh]))
    setSeen(next)
    saveSeenAnnouncements(next)
      .then((response) => {
        if (response?.tk) {
          saveAuth?.(response)
          setCurrentUser?.(response)
        }
      })
      .catch(() => {
        // Not stored: the icon stays still for this session and shakes again in a later one.
      })
  }

  return (
    <div className={clsx('app-navbar-item', itemClassName)}>
      <Dropdown
        show={open}
        onToggle={(next) => {
          if (next) markSeen()
          setOpen(next)
        }}
        align='end'
        className='wn'
      >
        <Dropdown.Toggle
          as='button'
          type='button'
          bsPrefix='wn__toggle'
          className={clsx('btn btn-icon position-relative', buttonClassName, {
            'wn__toggle--fresh': unseen.length > 0,
          })}
          title={unseen.length > 0 ? 'امکانات تازه؛ مورد جدیدی اضافه شده است' : 'امکانات تازه'}
          aria-label='امکانات تازه'
          aria-describedby={unseen.length > 0 ? 'wn-fresh-note' : undefined}
        >
          <i className='las la-gift fs-1' aria-hidden='true' />
          {unseen.length > 0 && (
            <span className='wn__dot'>
              <span id='wn-fresh-note' className='visually-hidden'>
                مورد جدیدی اضافه شده است
              </span>
            </span>
          )}
        </Dropdown.Toggle>
        <Dropdown.Menu className='notify-menu wn__menu' renderOnMount={false}>
          <div className='notify-menu__header'>
            <span>امکانات تازه</span>
          </div>
          <ul className='wn__list'>
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type='button'
                  className='wn__item'
                  onClick={() => {
                    setOpen(false)
                    item.open()
                  }}
                >
                  <span className='wn__icon' aria-hidden='true'>
                    <i className={`las ${item.icon}`} />
                  </span>
                  <span className='wn__main'>
                    <strong>
                      {item.title}
                      {item.announcement && freshInList.includes(item.announcement) && (
                        <span className='wn__badge'>جدید</span>
                      )}
                    </strong>
                    <small>{item.text}</small>
                  </span>
                  <i className='las la-angle-left wn__go' aria-hidden='true' />
                </button>
              </li>
            ))}
          </ul>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  )
}
